import React, { useState, useEffect, useMemo } from 'react';
import { Layout } from './components/dashboard/layout';
import { useAuth } from './App'; // or your actual path to AuthProvider

const MyStaffAttendance = () => {
  const { user } = useAuth?.() || {};
  const [staff, setStaff] = useState([]);                 // array with at most 1 item (me)
  const [departmentSummary, setDepartmentSummary] = useState([]); // array with at most 1 dept (mine)
  const [activeTab, setActiveTab] = useState(0);
  const [isLoading, setIsLoading] = useState(false);
  const [lastUpdated, setLastUpdated] = useState(null);
  const [error, setError] = useState('');

  const email = useMemo(() => {
    // prefer context
    if (user && user.email) return String(user.email).toLowerCase();
    // fallback to localStorage
    try {
      const ud = JSON.parse(localStorage.getItem('userData') || '{}');
      return (ud && ud.email) ? String(ud.email).toLowerCase() : '';
    } catch (e) {
      return '';
    }
  }, [user]);

  const API_BASE = 'https://raptornet.aischennai.org/screening-manager';

  async function fetchData() {
    if (!email) {
      setError('User email not found. Please sign in again.');
      setStaff([]);
      return;
    }
    setError('');
    setIsLoading(true);
    try {
      const url = `${API_BASE}/gate-api/getMyStaffAttendance.php`; // <-- gate-api, https in prod
      const res = await fetch(url, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
        body: JSON.stringify({ email })
      });

      // guard: make sure we didn't get index.html
      const ct = res.headers.get('content-type') || '';
      if (!res.ok) {
        const text = await res.text();
        throw new Error(`HTTP ${res.status}: ${text.slice(0,200)}`);
      }
      if (!ct.includes('application/json')) {
        const text = await res.text();
        throw new Error(`Non-JSON (likely SPA index): ${text.slice(0,200)}`);
      }

      const staffJson = await res.json();
      setStaff(staffJson && staffJson.success && Array.isArray(staffJson.data) ? staffJson.data : []);
      setLastUpdated(new Date());
    } catch (err) {
      console.error(err);
      setError('Failed to load data.');
      setStaff([]);
    } finally {
      setIsLoading(false);
    }
  }

  useEffect(() => { fetchData(); /* eslint-disable-next-line */ }, [email]);

  const getAttendanceStatus = (checkinTime, checkoutTime) => {
    if (checkinTime && checkoutTime) return 'Checked Out';
    if (checkinTime) return 'On Campus';
    return 'Not Checked In';
  };

  const getStatusColor = (checkinTime, checkoutTime) => {
    if (checkinTime && checkoutTime) return '#f44336';
    if (checkinTime) return '#4caf50';
    return '#ff9800';
  };

  const styles = {
    container: { padding: '2rem', maxWidth: '100%' },
    header: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '2rem' },
    title: { fontSize: '2rem', fontWeight: 'bold', margin: 0, color: '#333' },
    refreshButton: { padding: '10px 20px', backgroundColor: '#1976d2', color: '#fff', border: 'none', borderRadius: '4px', cursor: 'pointer', fontSize: '14px', minWidth: '120px' },
    refreshButtonDisabled: { backgroundColor: '#ccc', cursor: 'not-allowed' },
    lastUpdated: { fontSize: '0.875rem', color: '#666', marginBottom: '1rem' },
    error: { color: '#a50e0e', marginBottom: '1rem' },
    tabs: { display: 'flex', marginBottom: '2rem', borderBottom: '2px solid #e0e0e0' },
    tab: { padding: '12px 24px', backgroundColor: 'transparent', border: 'none', cursor: 'pointer', fontSize: '16px', borderBottom: '2px solid transparent', marginRight: '8px' },
    activeTab: { borderBottom: '2px solid #1976d2', color: '#1976d2', fontWeight: '600' },
    sectionTitle: { fontSize: '1.5rem', fontWeight: '600', marginBottom: '1rem', color: '#333' },
    summaryGrid: { display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))', gap: '1rem', marginBottom: '2rem' },
    summaryCard: { backgroundColor: '#fff', border: '1px solid #e0e0e0', borderRadius: '8px', padding: '1.5rem', textAlign: 'center', boxShadow: '0 2px 4px rgba(0,0,0,0.1)', minHeight: '120px', display: 'flex', flexDirection: 'column', justifyContent: 'center' },
    departmentTitle: { fontSize: '1.1rem', color: '#1976d2', marginBottom: '8px', fontWeight: '600' },
    attendanceNumber: { fontSize: '2rem', fontWeight: 'bold', color: '#4caf50', marginBottom: '4px' },
    attendanceText: { fontSize: '0.875rem', color: '#666', marginBottom: '4px' },
    tableContainer: { overflowX: 'auto', marginTop: '1rem' },
    table: { width: '100%', borderCollapse: 'collapse', fontSize: '14px', backgroundColor: '#fff', border: '1px solid #e0e0e0', borderRadius: '8px', overflow: 'hidden' },
    headerCell: { padding: '12px 16px', borderBottom: '2px solid #ddd', fontWeight: '600', color: '#333', backgroundColor: '#f8f9fa', whiteSpace: 'nowrap' },
    cell: { padding: '10px 16px', borderBottom: '1px solid #eee', color: '#444', whiteSpace: 'nowrap' },
    evenRow: { backgroundColor: '#fafafa' },
    oddRow: { backgroundColor: '#fff' },
    noData: { textAlign: 'center', padding: '20px', color: '#666' }
  };

  return (
    <Layout>
      <div style={styles.container}>
        <div style={styles.header}>
          <h1 style={styles.title}>My Attendance</h1>
          <button
            style={{ ...styles.refreshButton, ...(isLoading ? styles.refreshButtonDisabled : {}) }}
            onClick={fetchData}
            disabled={isLoading}
          >
            {isLoading ? 'Refreshing...' : 'Refresh'}
          </button>
        </div>

        {error ? <div style={styles.error}>{error}</div> : null}
        {lastUpdated && <div style={styles.lastUpdated}>Last updated: {lastUpdated.toLocaleTimeString()}</div>}

        <div style={styles.tabs}>
          <button style={{ ...styles.tab, ...(activeTab===0 ? styles.activeTab : {}) }} onClick={() => setActiveTab(0)}>My Department Summary</button>
          <button style={{ ...styles.tab, ...(activeTab===1 ? styles.activeTab : {}) }} onClick={() => setActiveTab(1)}>My Details</button>
        </div>

        {activeTab === 0 && (
          <div>
            <h2 style={styles.sectionTitle}>Department Attendance Summary (Today)</h2>
            {/*departmentSummary.length === 0 ? (
              <div style={styles.noData}>No summary available.</div>
            ) : (
              <>
                <div style={styles.summaryGrid}>
                  {departmentSummary.map((dept, idx) => (
                    <div key={idx} style={styles.summaryCard}>
                      <div style={styles.departmentTitle}>{dept.department || 'Unknown Department'}</div>
                      <div style={styles.attendanceNumber}>{dept.checked_in_staff || 0}</div>
                      <div style={styles.attendanceText}>of {dept.total_staff || 0} staff present</div>
                      <div style={{ ...styles.attendanceText, fontSize: '0.75rem' }}>({dept.checkin_percentage || 0}% attendance)</div>
                    </div>
                  ))}
                </div>

                <div style={styles.tableContainer}>
                  <table style={styles.table}>
                    <thead>
                      <tr>
                        <th style={styles.headerCell}>Department</th>
                        <th style={styles.headerCell}>Total Staff</th>
                        <th style={styles.headerCell}>Checked In</th>
                        <th style={styles.headerCell}>Checked Out</th>
                        <th style={styles.headerCell}>Not Checked In</th>
                        <th style={styles.headerCell}>Still on Campus</th>
                        <th style={styles.headerCell}>Check-in %</th>
                        <th style={styles.headerCell}>Check-out %</th>
                      </tr>
                    </thead>
                    <tbody>
                      {departmentSummary.map((dept, idx) => (
                        <tr key={idx} style={idx % 2 === 0 ? styles.evenRow : styles.oddRow}>
                          <td style={{ ...styles.cell, fontWeight: 600 }}>{dept.department || '-'}</td>
                          <td style={styles.cell}>{dept.total_staff || 0}</td>
                          <td style={{ ...styles.cell, color: '#4caf50', fontWeight: 600 }}>{dept.checked_in_staff || 0}</td>
                          <td style={{ ...styles.cell, color: '#f44336', fontWeight: 600 }}>{dept.checked_out_staff || 0}</td>
                          <td style={{ ...styles.cell, color: '#ff9800', fontWeight: 600 }}>{dept.not_checked_in || 0}</td>
                          <td style={{ ...styles.cell, color: '#2196f3', fontWeight: 600 }}>{dept.still_on_campus || 0}</td>
                          <td style={styles.cell}>{dept.checkin_percentage || 0}%</td>
                          <td style={styles.cell}>{dept.checkout_percentage || 0}%</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </>
            )*/}
          </div>
        )}

        {activeTab === 1 && (
          <div>
            <h2 style={styles.sectionTitle}>My Attendance Details (Today)</h2>
            <div style={styles.tableContainer}>
              <table style={styles.table}>
                <thead>
                  <tr>
                    <th style={styles.headerCell}>PS Code</th>
                    <th style={styles.headerCell}>Department</th>
                    <th style={styles.headerCell}>First Name</th>
                    <th style={styles.headerCell}>Last Name</th>
                    <th style={styles.headerCell}>Job Title</th>
                    <th style={styles.headerCell}>Status</th>
                    <th style={styles.headerCell}>Check-In Time</th>
                    <th style={styles.headerCell}>Check-In Device</th>
                    <th style={styles.headerCell}>Check-Out Time</th>
                    <th style={styles.headerCell}>Check-Out Device</th>
                  </tr>
                </thead>
                <tbody>
                  {staff.length === 0 ? (
                    <tr><td colSpan="10" style={styles.noData}>{isLoading ? 'Loading...' : 'No data.'}</td></tr>
                  ) : staff.map((s, idx) => (
                    <tr key={idx} style={idx % 2 === 0 ? styles.evenRow : styles.oddRow}>
                      <td style={styles.cell}>{s.ps_code || '-'}</td>
                      <td style={styles.cell}>{s.department || '-'}</td>
                      <td style={styles.cell}>{s.first_name || '-'}</td>
                      <td style={styles.cell}>{s.last_name || '-'}</td>
                      <td style={styles.cell}>{s.job_title || '-'}</td>
                      <td style={{ ...styles.cell, color: getStatusColor(s.checkin_time, s.checkout_time), fontWeight: 600 }}>
                        {getAttendanceStatus(s.checkin_time, s.checkout_time)}
                      </td>
                      <td style={styles.cell}>{s.checkin_time ? new Date(s.checkin_time).toLocaleTimeString() : '-'}</td>
                      <td style={styles.cell}>{s.checkin_device_name || '-'}</td>
                      <td style={styles.cell}>{s.checkout_time ? new Date(s.checkout_time).toLocaleTimeString() : '-'}</td>
                      <td style={styles.cell}>{s.checkout_device_name || '-'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </div>
    </Layout>
  );
};

export default MyStaffAttendance;
